import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { getFundamentalColor } from '@/api/recognition';
import { excelColors, argbACss } from '@/utils/excelTheme';
import type { FundamentalCatalogItem } from '@/api/types';

/**
 * Resolutores de nombre/color de Fundamental y Hábito para reportería (`ShineTab.tsx`/
 * `DailyTab.tsx`): mismos cuatro mapas, extraídos aquí para que las dos pestañas no mantengan copias
 * verbatim que puedan divergir. Del catálogo CON inactivos (`useFundamentalsForReporting`, no
 * `useFundamentals`): estas tablas enseñan Fundamentales/Hábitos HISTÓRICOS, y con el catálogo activo
 * uno retirado caería a su id crudo.
 */
export function useFundamentalNaming(
  catalogoReporteria: FundamentalCatalogItem[] | undefined,
  lang: string,
  t: TFunction<'recognition'>,
) {
  /** Nombre del hábito. */
  const nombreDeHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) {
        porId.set(h.id, h.names?.[lang] ?? h.names?.en ?? h.code ?? h.id);
      }
    }
    return (id: string) => porId.get(id) ?? id;
  }, [catalogoReporteria, lang]);

  /** Nombre del Fundamental. Sin nombre NO se enseña el id: se rotula como retirado. */
  const nombreDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      porId.set(f.id, f.names?.[lang] ?? f.names?.en ?? f.code ?? f.id);
    }
    return (id: string) => porId.get(id) ?? t('reports.daily.retiredFundamental');
  }, [catalogoReporteria, lang, t]);

  /** Color de cada Fundamental para la gráfica de barras del export — el mismo color guardado en
   *  catálogo que ya usa `<DistribucionFundamental>` en pantalla, no uno inventado. */
  const colorDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    (catalogoReporteria ?? []).forEach((f, indice) => {
      porId.set(f.id, getFundamentalColor(f as never, indice));
    });
    return (id: string) => porId.get(id) ?? argbACss(excelColors.headerTablaFondo);
  }, [catalogoReporteria]);

  /** Fundamental padre de cada hábito. */
  const padreDelHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) porId.set(h.id, f.id);
    }
    return porId;
  }, [catalogoReporteria]);

  return {
    nombreDeHabito, nombreDeFundamental, colorDeFundamental, padreDelHabito,
  };
}
